// @ts-nocheck
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { useState, useEffect } from 'react';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  List,
  Stack,
  Button,
  Drawer,
  Divider,
  Link,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListItemButton
} from '@mui/material';
// routes
import { PATH_AUTH } from '../../routes/paths';
// components
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import { MIconButton } from '../../components/@material-extend';
//
import menuConfig from './MenuConfig';

// ----------------------------------------------------------------------

const ICON_SIZE = 22;
const ITEM_SIZE = 48;
const PADDING = 2.5;

const ListItemStyle = styled(ListItemButton)(({ theme }) => ({
  ...theme.typography.body2,
  height: ITEM_SIZE,
  textTransform: 'capitalize',
  marginBottom: theme.spacing(0.5),
  marginLeft: theme.spacing(1.5),
  marginRight: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
  transition: theme.transitions.create('all', {
    duration: theme.transitions.duration.shorter
  }),
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover
  }
}));

// ----------------------------------------------------------------------

MenuMobileItem.propTypes = {
  item: PropTypes.object,
  isOpen: PropTypes.bool,
  isActive: PropTypes.bool,
  onOpen: PropTypes.func
};

function MenuMobileItem({ item, isOpen, isActive, onOpen }) {
  const { title, path, icon, children } = item;

  if (children) {
    return (
      <div key={title}>
        <ListItemStyle onClick={onOpen}>
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText disableTypography primary={title} />
          <Box
            component={Icon}
            icon={isOpen ? arrowIosDownwardFill : arrowIosForwardFill}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box sx={{ pb: 1 }}>
            {children.map((group) => (
              <List key={group.subheader} disablePadding sx={{ mb: 0.5 }}>
                <Box
                  component="li"
                  sx={(theme) => ({
                    pl: PADDING,
                    mt: 1,
                    mb: 0.5,
                    ...theme.typography.overline,
                    display: 'block',
                    color: 'text.disabled'
                  })}
                >
                  {group.subheader}
                </Box>
                {group.items.map((link) => (
                  <ListItemButton
                    key={link.title}
                    to={link.path}
                    component={RouterLink}
                    sx={(theme) => ({
                      ...theme.typography.body2,
                      height: 40,
                      mx: 1.5,
                      pl: 2.5,
                      borderRadius: 1,
                      textTransform: 'capitalize',
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                      '&.active': {
                        color: 'primary.main',
                        fontWeight: 'fontWeightMedium',
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)
                      }
                    })}
                  >
                    <ListItemText disableTypography primary={link.title} />
                  </ListItemButton>
                ))}
              </List>
            ))}
          </Box>
        </Collapse>
      </div>
    );
  }

  if (title === 'Documentation') {
    return (
      <ListItemStyle
        href={path}
        target="_blank"
        component={Link}
        sx={{
          ...(isActive && {
            color: 'primary.main',
            fontWeight: 'fontWeightMedium',
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)
          })
        }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText disableTypography primary={title} />
      </ListItemStyle>
    );
  }

  return (
    <ListItemStyle
      to={path}
      component={RouterLink}
      sx={{
        ...(isActive && {
          color: 'primary.main',
          fontWeight: 'fontWeightMedium',
          bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)
        })
      }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText disableTypography primary={title} />
    </ListItemStyle>
  );
}

MenuMobile.propTypes = {
  isOffset: PropTypes.bool,
  isHome: PropTypes.bool
};

export default function MenuMobile({ isOffset, isHome }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (mobileOpen) {
      handleDrawerClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleDrawerOpen = () => {
    setMobileOpen(true);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleOpen = () => {
    setOpen(!open);
  };

  return (
    <>
      <MIconButton
        onClick={handleDrawerOpen}
        sx={{
          ml: 1,
          ...(isHome && { color: 'common.white' }),
          ...(isOffset && { color: 'text.primary' })
        }}
      >
        <Icon icon={menu2Fill} />
      </MIconButton>

      <Drawer
        open={mobileOpen}
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: 280, borderRadius: 0 } }}
      >
        <Stack sx={{ height: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ pl: 2.5, pr: 1.5, py: 2.5 }}
          >
            <Link component={RouterLink} to="/" sx={{ display: 'inline-flex' }}>
              <Logo />
            </Link>
            <MIconButton onClick={handleDrawerClose} sx={{ color: 'text.secondary' }}>
              <Icon icon={closeFill} width={22} height={22} />
            </MIconButton>
          </Stack>

          <Divider sx={{ borderStyle: 'dashed' }} />

          <Scrollbar sx={{ flexGrow: 1, height: 'auto' }}>
            <List disablePadding sx={{ py: 2 }}>
              {menuConfig.map((link) => (
                <MenuMobileItem
                  key={link.title}
                  item={link}
                  isOpen={open}
                  onOpen={handleOpen}
                  isActive={pathname === link.path}
                />
              ))}
            </List>
          </Scrollbar>

          <Box sx={{ p: 2.5 }}>
            <Stack spacing={1.5}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                component={RouterLink}
                to={PATH_AUTH.register}
              >
                Register
              </Button>
              <Button
                fullWidth
                size="large"
                color="inherit"
                variant="outlined"
                component={RouterLink}
                to={PATH_AUTH.login}
                sx={{ color: 'text.primary', borderColor: 'grey.500_32' }}
              >
                Login
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Drawer>
    </>
  );
}